import { useState, useEffect, useCallback } from 'react';
import {
  getLinkPageById,
  updateLinkPage,
  addLinkToPage,
  removeLinkFromPage,
  reorderLinks,
  type UpdateLinkPageInput,
} from '@/services/linkpages';
import type { LinkPage, LinkPageLink } from '@/types';

export const useLinkPage = (id: string | undefined) => {
  const [linkPage, setLinkPage] = useState<LinkPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLinkPage = useCallback(async () => {
    if (!id) {
      setLinkPage(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getLinkPageById(id);
      setLinkPage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch link page');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLinkPage();
  }, [fetchLinkPage]);

  const update = async (input: UpdateLinkPageInput) => {
    if (!id) throw new Error('No link page ID');
    const updated = await updateLinkPage(id, input);
    setLinkPage(updated);
    return updated;
  };

  const addLink = async (link: Omit<LinkPageLink, 'id' | 'order'>) => {
    if (!id) throw new Error('No link page ID');
    const updated = await addLinkToPage(id, link);
    setLinkPage(updated);
    return updated;
  };

  const removeLink = async (linkId: string) => {
    if (!id) throw new Error('No link page ID');
    const updated = await removeLinkFromPage(id, linkId);
    setLinkPage(updated);
    return updated;
  };

  const reorder = async (linkIds: string[]) => {
    if (!id) throw new Error('No link page ID');
    const updated = await reorderLinks(id, linkIds);
    setLinkPage(updated);
    return updated;
  };

  return {
    linkPage,
    loading,
    error,
    update,
    addLink,
    removeLink,
    reorderLinks: reorder,
    refresh: fetchLinkPage,
  };
};
